"use client";

import { motion } from "motion/react";
import { PropertyDetailMap } from "./property-detail-map";
import { Trophy, ShoppingBag, Building2, Plane, type LucideIcon } from "lucide-react";
import type { PropertyDistance } from "@/types/database";

// Map icon names to Lucide components
const iconMap: Record<string, LucideIcon> = {
  Trophy,
  ShoppingBag,
  Building2,
  Plane,
};

// Define locally for display purposes
interface DistanceItem {
  id: string;
  label: string;
  time: string;
  icon: LucideIcon;
}

interface PropertyLocationProps {
  property: {
    id: number;
    name: string;
    location_name: string;
    latitude?: number | null;
    longitude?: number | null;
  };
  distances?: PropertyDistance[];
}

// Default distances for fallback
const defaultDistances: DistanceItem[] = [
  { id: "rugby-sevens", label: "Dubai Rugby Sevens", time: "5 Minutes", icon: Trophy },
  { id: "outlet-mall", label: "Dubai Outlet Mall", time: "8 Minutes", icon: ShoppingBag },
  { id: "downtown", label: "Downtown Dubai", time: "20 Minutes", icon: Building2 },
  { id: "dxb-airport", label: "Dubai International Airport", time: "25 Minutes", icon: Plane },
  { id: "dwm-airport", label: "Al Maktoum International Airport", time: "30 Minutes", icon: Plane },
];

export function PropertyLocation({ property, distances }: PropertyLocationProps) {
  // Convert database distances to display format, or use defaults
  const displayDistances: DistanceItem[] = distances && distances.length > 0
    ? distances.map(d => ({
      id: String(d.id),
      label: d.landmark_name,
      time: d.distance_time,
      icon: iconMap[d.icon] ?? Building2,
    }))
    : defaultDistances;

  return (
    <section className="py-24 px-6 md:px-12 max-w-[1400px] mx-auto">
      <div className="text-center mb-24 space-y-16">
        <div className="space-y-4">
          <span className="text-xs font-bold tracking-[0.3em] text-secondary uppercase block">
            The Neighborhood
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-primary">
            Vibrant Living, Closer to <span className="italic text-primary/50">Convenience</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-16">
          {displayDistances.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center text-center space-y-4 p-6 bg-secondary/10 rounded-xl hover:bg-secondary/20 transition-colors"
              >
                <IconComponent className="w-10 h-10 text-primary/60 stroke-[1]" />
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 h-8 flex items-center justify-center">
                    {item.label}
                  </h4>
                  <p className="font-serif text-xl text-primary">{item.time}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="h-[500px] w-full bg-secondary/10 rounded-xl overflow-hidden shadow-lg border border-primary/10">
        <PropertyDetailMap property={property} />
      </div>
    </section>
  );
}
